import React, { useEffect, useRef, useState } from "react";
import { FormControl, FormHelperText, TextField } from "@mui/material";
import StackWrapper from "../../StackWrapper";
import Label from "../../../../../common/components/Label";
import { COLOR_MAP } from "../../../../../constants/ColorNames";
import SelectWrapper from "../../SelectWrapper";
import { TNoticePeriod } from "../../../../../types/campaign.types";
import { numberCheckRegex } from "../../../../../utils/whatsAppUtils";

interface IProps {
  error?: string;
  isRequired?: boolean;
  noticePeriod: string;
  handleUpdateNoticePeriod: (value: string) => void;
}

const getNoticePeriod = (
  noticePeriod: string,
  noticePeriodDuration: string
) => {
  if (Number(noticePeriod)) {
    if (noticePeriodDuration === "days") {
      if (Number(noticePeriod) > 1) {
        return `${noticePeriod} days`;
      }
      return `${noticePeriod} day`;
    }

    if (Number(noticePeriod) > 1) {
      return `${noticePeriod} months`;
    }
    return `${noticePeriod} month`;
  }
  return "";
};

const NoticePeriod: React.FC<IProps> = ({
  error,
  isRequired = false,
  noticePeriod,
  handleUpdateNoticePeriod,
}) => {
  const [noticePeriodState, setNoticePeriodState] = useState<string>("");
  const [noticePeriodDuration, setNoticePeriodDuration] =
    useState<TNoticePeriod>("days");
  const isFirstRender = useRef<boolean>(true);

  const handleChangeNoticePeriod = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;

    if (!value) {
      setNoticePeriodState("");
      handleUpdateNoticePeriod("");
    } else if (numberCheckRegex.test(value)) {
      setNoticePeriodState(value);
      handleUpdateNoticePeriod(getNoticePeriod(value, noticePeriodDuration));
    }
  };

  const handleChangeNoticePeriodDuration = (newValue: string) => {
    const duration: TNoticePeriod = newValue
      ? (newValue as TNoticePeriod)
      : "days";
    setNoticePeriodDuration(duration);
    handleUpdateNoticePeriod(getNoticePeriod(noticePeriodState, newValue));
  };

  useEffect(() => {
    if (noticePeriod && !noticePeriodState && isFirstRender.current) {
      const [value, duration] = noticePeriod.split(" ") || ["", "days"];
      setNoticePeriodState(value || "");
      setNoticePeriodDuration(
        ["day", "days"]?.includes(duration?.trim()) ? "days" : "months"
      );
      isFirstRender.current = false;
    }
  }, [noticePeriod, noticePeriodState]);

  return (
    <StackWrapper>
      <Label
        colorType="light"
        label="Notice Period"
        required={!!isRequired}
        labelFor="noticePeriod"
      />
      <FormControl
        fullWidth
        sx={{
          display: "flex",
          flexDirection: "row",
          border: error
            ? `1px solid ${COLOR_MAP.ERROR}`
            : `1px solid ${COLOR_MAP.BORDER_GREY}`,
          borderRadius: "4px",
          ...(!!error
            ? {
                borderColor: COLOR_MAP.ERROR,
              }
            : {}),
        }}
      >
        <TextField
          variant="outlined"
          value={noticePeriodState}
          onChange={handleChangeNoticePeriod}
          fullWidth
          margin="normal"
          placeholder="Notice Period"
          error={!!error}
          helperText={!!error || ""}
          autoComplete="off"
          sx={{
            "& fieldset": {
              border: "none",
              borderRight: `1px solid ${COLOR_MAP.BORDER_GREY}`,
              borderBottomRightRadius: 0,
              borderTopRightRadius: 0,
            },
            m: 0,
            width: "75%",
          }}
        />
        <SelectWrapper
          handleChange={handleChangeNoticePeriodDuration}
          placeholder="Max"
          menuItems={[
            { label: "Days", value: "days" },
            { label: "Months", value: "months" },
          ]}
          renderValue={(value) => {
            const selectedItem = [
              { label: "Days", value: "days" },
              { label: "Months", value: "months" },
            ].find((item) => item.value === value);

            return selectedItem ? selectedItem.label : value;
          }}
          value={noticePeriodDuration}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            width: "25%",
            color: COLOR_MAP.GREY_5,
            background: COLOR_MAP.GREY_9,
          }}
        />
      </FormControl>
      {!!error && <FormHelperText color="error">{error}</FormHelperText>}
    </StackWrapper>
  );
};

export default NoticePeriod;
